/**
 * Fuente única del árbol de navegación administrativa — plan 27_09_2026 §4.2.
 *
 * Antes de este archivo, el menú vivía copiado dentro de cada vista: tres
 * bloques `#menu`/`.menu-group` idénticos (AdminPage, Gastos, Ingresos) y
 * cuatro barras de pestañas idénticas en Print3DAdmin/*. Añadir un módulo
 * obligaba a tocar entre 4 y 7 archivos `.vue`.
 *
 * Ahora: se añade una entrada aquí y una ruta en el router. Nada más.
 * Lo consumen AdminSidebar (navegación) y AdminPageHeader (migas y título).
 *
 * Cada ítem se identifica por `name`, que debe coincidir con el `name` de la
 * ruta en `router/index.js`. La prueba de consistencia de §7.1 verifica que
 * toda ruta con `requiresAuth` tenga aquí su entrada, y viceversa.
 */

import {
  LayoutDashboard,
  Building2,
  UserCog,
  TrendingUp,
  TrendingDown,
  Inbox,
  Package,
  Layers,
  Tags,
} from 'lucide-vue-next';

export const adminNav = [
  {
    section: 'General',
    items: [
      { name: 'Admin', label: 'Panel', to: '/admin', icon: LayoutDashboard },
      {
        name: 'AdminEmpresa',
        label: 'Empresa',
        to: { path: '/admin', query: { vista: 'empresa' } },
        icon: Building2,
        // Vista interna de AdminPage: comparte ruta con el panel y se
        // selecciona por query string, para no inventar URLs nuevas
        // (el plan deja fuera de alcance cambiar rutas).
        matchQuery: { vista: 'empresa' },
      },
      {
        name: 'AdminUsuario',
        label: 'Usuarios',
        to: { path: '/admin', query: { vista: 'usuario' } },
        icon: UserCog,
        matchQuery: { vista: 'usuario' },
      },
    ],
  },
  {
    section: 'Finanzas',
    items: [
      { name: 'Dashboard', label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
      { name: 'Ingresos', label: 'Ingresos', to: '/ingresos', icon: TrendingUp },
      { name: 'Gastos', label: 'Gastos', to: '/gastos', icon: TrendingDown },
    ],
  },
  {
    section: 'Impresión 3D',
    items: [
      { name: 'Print3DOrdenes', label: 'Órdenes', to: '/admin/print3d/ordenes', icon: Inbox },
      { name: 'Print3DCatalogo', label: 'Catálogo', to: '/admin/print3d/catalogo', icon: Package },
      { name: 'Print3DMateriales', label: 'Materiales', to: '/admin/print3d/materiales', icon: Layers },
      { name: 'Print3DCategorias', label: 'Categorías', to: '/admin/print3d/categorias', icon: Tags },
    ],
  },
];

/** Todos los ítems en una sola lista, sin agrupar. */
export function flatNav() {
  return adminNav.flatMap((group) =>
    group.items.map((item) => ({ ...item, section: group.section })),
  );
}

/**
 * Ítem correspondiente a una ruta activa. Las vistas internas de AdminPage se
 * distinguen por query string, así que se comprueba primero la coincidencia
 * más específica (nombre + query) y luego la simple por nombre.
 */
export function findNavItem(route) {
  const items = flatNav();
  const byQuery = items.find(
    (item) =>
      item.matchQuery &&
      Object.entries(item.matchQuery).every(([k, v]) => route.query?.[k] === v),
  );
  if (byQuery) return byQuery;

  // Sin query reconocida, el ítem base de la ruta es el que no exige query.
  return items.find((item) => item.name === route.name && !item.matchQuery) || null;
}

/**
 * Migas de pan para AdminPageHeader: `Admin › Sección › Vista`.
 * El primer nivel siempre enlaza al panel; el último es la vista actual.
 */
export function breadcrumbsFor(route) {
  const item = findNavItem(route);
  const crumbs = [{ label: 'Admin', to: '/admin' }];
  if (!item) return crumbs;
  if (item.section !== 'General') crumbs.push({ label: item.section, to: null });
  if (item.to !== '/admin' || item.matchQuery) crumbs.push({ label: item.label, to: null });
  return crumbs;
}

export default adminNav;
